/**
 * Animation Utility Functions
 * Common animation patterns and helper functions
 */

import type { Variants, Transition } from 'framer-motion';
import type { SpringConfig } from '@react-spring/core';
import type { AnimationConfig } from './config';

/**
 * Creates entrance animation variants for Framer Motion
 */
export const createEntranceVariants = (
  config: AnimationConfig,
  direction: 'up' | 'down' | 'left' | 'right' | 'fade' = 'fade'
): Variants => {
  const { customTimings, reducedMotion } = config;
  
  if (reducedMotion) {
    return {
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    };
  }

  const baseTransition: Transition = {
    duration: customTimings.normal,
    ease: customTimings.defaultEasing,
  };

  const directionVariants = {
    up: {
      hidden: { opacity: 0, y: 50 },
      visible: { opacity: 1, y: 0, transition: baseTransition },
    },
    down: {
      hidden: { opacity: 0, y: -50 },
      visible: { opacity: 1, y: 0, transition: baseTransition },
    },
    left: {
      hidden: { opacity: 0, x: 50 },
      visible: { opacity: 1, x: 0, transition: baseTransition },
    },
    right: {
      hidden: { opacity: 0, x: -50 },
      visible: { opacity: 1, x: 0, transition: baseTransition },
    },
    fade: {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: baseTransition },
    },
  };

  return directionVariants[direction];
};

/**
 * Creates staggered animation variants for lists
 */
export const createStaggerVariants = (
  config: AnimationConfig,
  staggerDelay: number = 0.1
): Variants => {
  const { customTimings, reducedMotion } = config;
  
  if (reducedMotion) {
    return {
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    };
  }

  return {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: customTimings.fast,
      },
    },
  };
};

/**
 * Creates hover animation variants
 */
export const createHoverVariants = (
  config: AnimationConfig,
  effect: 'lift' | 'scale' | 'glow' | 'tilt' = 'lift'
): Variants => {
  const { customTimings, reducedMotion, enableComplexEffects } = config;
  
  if (reducedMotion) {
    return {
      rest: {},
      hover: {},
    };
  }

  const baseTransition: Transition = {
    duration: customTimings.fast,
    ease: 'easeOut',
  };

  const effectVariants = {
    lift: {
      rest: { y: 0, scale: 1 },
      hover: { 
        y: -8, 
        scale: 1.02, 
        transition: baseTransition,
      },
    },
    scale: {
      rest: { scale: 1 },
      hover: { 
        scale: 1.05, 
        transition: baseTransition,
      },
    },
    glow: {
      rest: { 
        boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
      },
      hover: { 
        boxShadow: enableComplexEffects 
          ? '0 10px 30px rgba(0, 0, 0, 0.2)' 
          : '0 5px 15px rgba(0, 0, 0, 0.1)',
        transition: baseTransition,
      },
    },
    tilt: {
      rest: { rotateX: 0, rotateY: 0 },
      hover: enableComplexEffects ? { 
        rotateX: -5, 
        rotateY: 5, 
        transition: baseTransition,
      } : {
        scale: 1.02,
        transition: baseTransition,
      },
    },
  };

  return effectVariants[effect];
};

/**
 * Creates loading animation variants
 */
export const createLoadingVariants = (config: AnimationConfig): Variants => {
  const { customTimings, reducedMotion } = config;
  
  if (reducedMotion) {
    return {
      loading: { opacity: 0.5 },
      loaded: { opacity: 1 },
    };
  }

  return {
    loading: {
      opacity: 0.7,
      scale: 0.98,
      transition: {
        repeat: Infinity,
        repeatType: 'reverse',
        duration: customTimings.slow,
      },
    },
    loaded: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: customTimings.normal,
      },
    },
  };
};

/**
 * Creates React Spring configuration based on animation config
 */
export const createSpringConfig = (
  config: AnimationConfig,
  preset: 'gentle' | 'wobbly' | 'stiff' | 'slow' = 'gentle'
): SpringConfig => {
  const { customTimings, reducedMotion } = config;
  
  if (reducedMotion) {
    return {
      tension: 300,
      friction: 50,
      mass: 1,
    };
  }

  const presets = {
    gentle: {
      tension: customTimings.springConfig.tension,
      friction: customTimings.springConfig.friction,
      mass: 1,
    },
    wobbly: {
      tension: 180,
      friction: 12,
      mass: 1,
    },
    stiff: {
      tension: 400,
      friction: 40,
      mass: 1,
    },
    slow: {
      tension: 200,
      friction: 50,
      mass: 3,
    },
  };

  return presets[preset];
};

/**
 * Creates scroll-triggered animation configuration
 */
export const createScrollAnimation = (
  config: AnimationConfig,
  threshold: number = 0.1
) => {
  const { reducedMotion } = config;
  
  return {
    threshold,
    triggerOnce: true,
    rootMargin: '0px 0px -100px 0px',
    skip: reducedMotion,
  };
};

/**
 * Debounce function for performance optimization
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Throttle function for scroll and resize events
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Check if element is in viewport
 */
export const isInViewport = (element: Element, threshold: number = 0): boolean => {
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;
  
  return (
    rect.top >= -threshold &&
    rect.left >= -threshold &&
    rect.bottom <= windowHeight + threshold &&
    rect.right <= windowWidth + threshold
  );
};

/**
 * Get optimal animation duration based on distance
 */
export const getOptimalDuration = (
  distance: number,
  config: AnimationConfig,
  minDuration: number = 0.2,
  maxDuration: number = 1.0
): number => {
  const { customTimings, reducedMotion } = config;
  
  if (reducedMotion) {
    return minDuration;
  }
  
  // Calculate duration based on distance (more distance = longer duration)
  const baseDuration = customTimings.normal;
  const calculatedDuration = Math.min(
    maxDuration,
    Math.max(minDuration, baseDuration * (distance / 100))
  );
  
  return calculatedDuration;
};